import React, { useState, useEffect } from 'react';

// --- STYLES ---
// Since we can't link an external CSS file in this environment,
// the styles are included directly in the component.
const LoginStyles = () => (
  <style>{`
    .login-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      background-color: #f7f7f7;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      padding: 20px;
      box-sizing: border-box;
    }
    .login-header {
      margin-bottom: 2rem;
      text-align: center;
    }
    .logo {
      max-width: 250px;
      height: auto;
    }
    .login-container h2 {
      color: #333;
      margin-bottom: 1.5rem;
      font-weight: 500;
    }
    .login-form {
      width: 100%;
      max-width: 400px;
      background: #fff;
      padding: 2.5rem;
      border-radius: 8px;
      box-shadow: 0 4px 15px rgba(0,0,0,0.08);
      box-sizing: border-box;
    }
    .form-group {
      margin-bottom: 1.5rem;
    }
    .form-group label {
      display: block;
      margin-bottom: 0.5rem;
      color: #555;
      font-weight: 600;
    }
    .form-group input[type="text"],
    .form-group input[type="password"] {
      width: 100%;
      padding: 0.75rem;
      border: 1px solid #ccc;
      border-radius: 4px;
      box-sizing: border-box;
      transition: border-color 0.2s;
    }
    .form-group input[type="text"]:focus,
    .form-group input[type="password"]:focus {
      outline: none;
      border-color: #007bff;
    }
    .show-password-container {
      display: flex;
      align-items: center;
      margin-top: 0.75rem;
    }
    .show-password-container input {
      margin-right: 0.5rem;
    }
    .show-password-container label {
      margin-bottom: 0;
      font-weight: normal;
      color: #666;
      font-size: 0.9em;
      cursor: pointer;
    }
    .login-button {
      width: 100%;
      padding: 0.85rem;
      background-color: #007bff;
      color: white;
      border: none;
      border-radius: 4px;
      font-size: 1rem;
      font-weight: 600;
      cursor: pointer;
      transition: background-color 0.3s ease;
    }
    .login-button:hover {
      background-color: #0056b3;
    }
    .login-message {
      margin-top: 1.5rem;
      padding: 0.75rem 1rem;
      border-radius: 4px;
      text-align: center;
      width: 100%;
      max-width: 400px;
      box-sizing: border-box;
      background-color: #e2e3e5;
      color: #383d41;
    }
  `}</style>
);

// The `process.env` object is not available in this browser-based environment.
// We are defining a mock URL here. In a real application built with a tool like
// Create React App, you would use environment variables.
const MOCK_API_URL = 'https://totallypainfree.com/riberana-backend-php';


// We'll rename the function to LoginDebug to make it clear this is the debug version.
function LoginDebug({ onLoginSuccess }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState('');
  const [apiUrlForDisplay, setApiUrlForDisplay] = useState('Not set yet');

  useEffect(() => {
    const url = MOCK_API_URL || '⚠️ MOCK_API_URL is NOT DEFINED!';
    setApiUrlForDisplay(url);
    console.log(`[DEBUG] Component Mounted. API URL is: ${url}`);
  }, []);

  // --- NEW FEATURE: Dedicated Error Logging ---
  // This function sends a detailed error report to a special logging script.
  const logErrorToServer = async (errorDetails) => {
    // This is the new endpoint for logging. You will need to create this file.
    const loggingUrl = `${apiUrlForDisplay}/api.php?path=logError`;
    console.log('[DEBUG] Logging error to server:', errorDetails);
    try {
      await fetch(loggingUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(errorDetails),
      });
    } catch (loggingError) {
      console.error('[DEBUG] 💥 Critical: Could not send error log to server.', loggingError);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage('Attempting login...');
    console.clear();
    console.log('--- [DEBUG] New Login Attempt ---');

    const apiUrl = `${apiUrlForDisplay}/api.php?path=login`;
    console.log(`[DEBUG]  costruito URL completo per la richiesta: ${apiUrl}`);

    // IMPORTANT: For security, we never log the actual password.
    const safeRequestBody = { email, password: '***' };
    console.log('[DEBUG] 📤 Dati inviati al server (password oscurata):', safeRequestBody);

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      console.log('[DEBUG] 📥 Risposta grezza dal server:', response);
      console.log(`[DEBUG] Stato della risposta: ${response.status} ${response.statusText}`);

      const result = await response.json();
      console.log('[DEBUG] 📦 Dati JSON analizzati dalla risposta:', result);

      if (response.ok && result.success) {
        const { user, token } = result.data;
        console.log('[DEBUG] ✅ Login Riuscito. Utente:', user, 'Token:', token);
        setMessage(`Login successful! Welcome, ${user.name}.`);
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('user_level', user.user_level);
        onLoginSuccess(user.user_level);

      } else {
        const errorMessage = result.error?.message || 'Login failed due to an unknown server issue.';
        setMessage(errorMessage + ' (Error has been logged)');
        console.error('[DEBUG] ❌ Errore di login (il backend ha segnalato un fallimento):', errorMessage);
        
        // --- Fire the new error logger ---
        logErrorToServer({
          timestamp: new Date().toISOString(),
          type: 'Backend Logic Error',
          errorMessage: errorMessage,
          requestUrl: apiUrl,
          requestBody: safeRequestBody,
          responseStatus: `${response.status} ${response.statusText}`,
          responseBody: result,
          userAgent: navigator.userAgent,
        });
      }
    } catch (error) {
      const errorMessage = error.message || 'Could not connect to the server.';
      console.error('[DEBUG] 💥 Errore di rete o di analisi JSON durante il login:', error);
      setMessage('Network Error. Could not connect. (Error has been logged)');
      
      // --- Fire the new error logger ---
      logErrorToServer({
        timestamp: new Date().toISOString(),
        type: 'Network or Parsing Error',
        errorMessage: errorMessage,
        requestUrl: apiUrl,
        requestBody: safeRequestBody,
        userAgent: navigator.userAgent,
      });
    }
  };

  return (
    <>
      <LoginStyles />
      <div className="login-container">
        <div className="login-header">
          <img src="https://storage.googleapis.com/proudcity/mebanenc/uploads/2021/03/placeholder-image.png" onError={(e) => { e.target.onerror = null; e.target.src='https://placehold.co/250x100/f7f7f7/333?text=Logo'; }} alt="Riberana Logo" className="logo" />
        </div>
        <h2>Ingresar al sistema (Debug Mode)</h2>

        <div style={{ padding: '10px', margin: '10px 0', border: '1px solid #ffc107', backgroundColor: '#fff3cd', color: '#664d03', borderRadius: '8px', textAlign: 'center', width: '100%', maxWidth: '400px', boxSizing: 'border-box' }}>
          <strong>API Target URL:</strong>
          <p style={{ margin: '5px 0 0 0', wordBreak: 'break-all' }}>{apiUrlForDisplay}</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="email">Usuario o email:</label>
            <input type="text" id="email" placeholder="Ingresar aqui" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div className="form-group">
            <label htmlFor="password">Contraseña:</label>
            <input type={showPassword ? 'text' : 'password'} id="password" placeholder="Ingresar aqui" value={password} onChange={(e) => setPassword(e.target.value)} required />
            <div className="show-password-container">
              <input type="checkbox" id="showPassword" checked={showPassword} onChange={() => setShowPassword(!showPassword)} />
              <label htmlFor="showPassword">Mostrar contraseña</label>
            </div>
          </div>
          <button type="submit" className="login-button">Login</button>
        </form>

        {message && <p className="login-message">{message}</p>}
      </div>
    </>
  );
}

export default LoginDebug;

